import { Point } from '@printu/geom';
import { isMachineZero } from './numerical';
export class Line {
    constructor(p1, p2) {
        Object.defineProperty(this, "p1", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "p2", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.p1 = new Point(p1);
        this.p2 = new Point(p2);
    }
    get length() {
        const dx = this.p2.x - this.p1.x;
        const dy = this.p2.y - this.p1.y;
        return Math.sqrt(dx * dx + dy * dy);
    }
    getDistance(point) {
        return Math.abs(this.getSignedDistance(point));
    }
    getSignedDistance(point) {
        return Line.getSignedDistance(this.p1, this.p2, point);
    }
    project(point) {
        const normal = this.p2.subtract(this.p1).normalized(1.0);
        const line = new Point(point).subtract(this.p1);
        return normal.dot(line);
    }
    inProximity(point, distance, minLength = 5) {
        if (this.getDistance(point) <= distance) {
            minLength = Math.max(0, minLength);
            const lineLength = this.length;
            const lengthRange = lineLength < minLength
                ? [0, minLength]
                : [-distance, lineLength + distance];
            const projectDistance = this.project(point);
            return (projectDistance >= lengthRange[0] &&
                projectDistance <= lengthRange[1]);
        }
        return false;
    }
    static getSide(point1, point2, point, asVector = false, isInfinite = false) {
        const { x, y } = point;
        const { x: px, y: py } = point1;
        let { x: vx, y: vy } = point2;
        if (!asVector) {
            vx -= px;
            vy -= py;
        }
        const v2x = x - px;
        const v2y = y - py;
        // ccw = v2.cross(v1);
        let ccw = v2x * vy - v2y * vx;
        if (!isInfinite && isMachineZero(ccw)) {
            // If the point is on the infinite line, check if it's on the
            // finite line too: Project v2 onto v1 and determine ccw based
            // on which side of the finite line the point lies. Calculate
            // the 'u' value of the point on the line, and use it for ccw:
            // u = v2.dot(v1) / v1.dot(v1)
            ccw = (v2x * vx + v2x * vx) / (vx * vx + vy * vy);
            // If the 'u' value is within the line range, set ccw to 0,
            // otherwise its already correct sign is all we need.
            if (ccw >= 0 && ccw <= 1) {
                ccw = 0;
            }
        }
        if (ccw < 0) {
            return -1;
        }
        if (ccw > 0) {
            return 1;
        }
        return 0;
    }
    static getSignedDistance(point1, point2, point, asVector = false) {
        const { x, y } = point;
        const { x: px, y: py } = point1;
        let { x: vx, y: vy } = point2;
        if (!asVector) {
            vx -= px;
            vy -= py;
        }
        // Based on the error analysis by @iconexperience outlined in #799
        if (vx === 0) {
            return vy > 0 ? x - px : px - x;
        }
        if (vy === 0) {
            return vx < 0 ? y - py : py - y;
        }
        return (((x - px) * vy - (y - py) * vx) /
            (vy > vx
                ? vy * Math.sqrt(1 + (vx * vx) / (vy * vy))
                : vx * Math.sqrt(1 + (vy * vy) / (vx * vx))));
    }
    static getDistance(point1, point2, point, asVector = false) {
        return Math.abs(Line.getSignedDistance(point1, point2, point, asVector));
    }
}
